import { FormEvent, useEffect, useRef } from 'react'
import { LoaderFunctionArgs, useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { useCurrentHealthCenter } from '../../components/AppShell'
import { createReport, fetchNewReport } from '../../api/reports'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { Button, Panel, PanelBody, PanelHeading } from '../../components/common'

/** The template's own HTML carries the report fields; autofill matches them by `report_<name>` id, as the legacy set_fields did. */
function applyAutofill(form: HTMLFormElement, autofill: Readonly<Record<string, string>>): void {
  Object.entries(autofill).forEach(([name, value]) => {
    const field = form.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#report_${CSS.escape(name)}`)
    if (field) {
      field.value = value
    }
  })
}

export function loader({ params, request }: LoaderFunctionArgs) {
  const reportTemplateId = new URL(request.url).searchParams.get('report_template')
  if (!reportTemplateId) {
    throw new ApiError(404, null)
  }
  return fetchNewReport(Number(params.healthCenterId), params.patientId as string, reportTemplateId)
}

export default function ReportFormPage() {
  const healthCenter = useCurrentHealthCenter()
  const navigate = useNavigate()
  const params = useParams()
  const patientId = params.patientId ?? ''
  const formRef = useRef<HTMLFormElement>(null)
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>

  useEffect(() => {
    if (formRef.current) {
      applyAutofill(formRef.current, data.autofill)
    }
  }, [data])

  if (!healthCenter) {
    return null
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const body = new FormData(event.currentTarget)
    body.append('report_template', String(data.reports_template.id))
    try {
      await createReport(healthCenter.id, patientId, body)
    } catch (error) {
      const isValidation = error instanceof ApiError && error.status === 422
      trackEvent('Form Save Failed', {
        form: 'report',
        reason: isValidation ? 'validation' : 'request',
        status_code: error instanceof ApiError ? error.status : undefined,
      })
      return
    }
    trackEvent('Report Created', { template_id: data.reports_template.id })
    navigate(`/health_centers/${healthCenter.id}/patients/${patientId}/reports`)
  }

  return (
    <Panel>
      <PanelHeading>
        <b>
          <h1 style={{ textAlign: 'center' }}>{`Nuevo ${data.reports_template.name} para ${data.patient.name ?? ''}`}</h1>
        </b>
      </PanelHeading>
      <PanelBody>
        <form ref={formRef} onSubmit={(event) => void handleSubmit(event)}>
          <div dangerouslySetInnerHTML={{ __html: data.reports_template.html_form }} />
          <Button type="submit">crear</Button>
        </form>
      </PanelBody>
    </Panel>
  )
}
